import queryString from "query-string";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { useCartServiceApi } from "@api/hooks/cartService.hook";
import { DefaultFor } from "@api/mappers/default.mapper";

import { DeliveryTypes, StorePickerModalStates } from "@utils/enums";

import { useThunkDispatch } from "@redux/dispatcher";
import { addConfigurableProductToCart } from "@redux/modules/api/data/cart/cart.module";
import { getPreferredStore } from "@redux/modules/api/data/preferredStore/preferredStore.selectors";
import { StoreDetailsModel } from "@redux/modules/api/data/stores/@types/storeDetails.model";
import { useGranite } from "@redux/modules/value/translations/translations.provider";
import { TApiAction } from "@redux/store.types";

import { StorePickerModal } from "@components/Store/StorePicker/storePickerModal.container";
import { StorePickerFeature } from "@components/Store/StorePicker/types/storePickerFeature.enum";
import { StorePickerType } from "@components/Store/StorePicker/types/storePickerType.enum";

import {
    handleMessage,
    sendFailMessage,
    sendSuccessMessage,
} from "./configurableProduct.client";
import "./configurableProduct.scss";

const FixedFeatures = [StorePickerFeature.BUY_ONLINE_PICKUP_IN_STORE];

type Props = {
    configUrl: string;
    endPoint: string;
    configEndPoint: string;
};

type ConfigurableProductConfigurationResponse = {
    defaultDeliveryType: DeliveryTypes;
};

const ConfigurableProductComponent = ({
    configUrl,
    endPoint = "/configurators/timber",
    configEndPoint = "/configurators/config/timber",
}: Props) => {
    const { t } = useGranite();

    const preferredStore = useSelector(getPreferredStore);
    const [storePickerModalState, setStorePickerModalState] = useState(
        StorePickerModalStates.Closed,
    );
    const dispatch = useThunkDispatch();
    const [iframeUrl, setIframeUrl] = useState<string>();
    const [cartData, setCartData] = useState();
    const [cartMessage, setCartMessage] = useState<any>();

    const iframeRef =
        useRef<HTMLIFrameElement>() as React.MutableRefObject<HTMLIFrameElement>;

    const [{ data }] = useCartServiceApi({
        url: configEndPoint,
        method: "GET",
        mapper: DefaultFor<ConfigurableProductConfigurationResponse>(),
    });

    useEffect(() => {
        if (configUrl) {
            const parsedQuery = queryString.parseUrl(configUrl);
            const parentQueryParams = queryString.parseUrl(location.search);
            const newQueryParams = {
                ...parsedQuery.query,
                ...parentQueryParams.query,
                clientHeight: document.body.clientHeight.toString(),
            };

            setIframeUrl(
                queryString.stringifyUrl({
                    ...parsedQuery,
                    query: newQueryParams,
                }),
            );
        }
    }, []);

    useEffect(() => {
        const messageHandler = (message: any) => {
            const domain = new URL(configUrl).host;

            if (
                !message.origin.includes(domain) ||
                !iframeRef.current?.contentWindow
            ) {
                return;
            }

            setCartMessage(message);
            handleMessage(
                message,
                iframeRef?.current,
                addToCartHandler,
                domain,
                data?.defaultDeliveryType,
            );
        };

        window.addEventListener("message", messageHandler);
        return () => {
            window.removeEventListener("message", messageHandler);
        };
    }, [configUrl, data?.defaultDeliveryType]);

    const addToCartHandler = (
        cartData: any,
        origin: string,
        defaultDeliveryType: DeliveryTypes,
    ) => {
        if (defaultDeliveryType === DeliveryTypes.ClickAndCollect) {
            setCartData(cartData);
            setStorePickerModalState(StorePickerModalStates.AddToCart);
        } else {
            handleStoreSelect(cartData, origin);
        }
    };

    const handleStoreSelect = useCallback(
        (
            configuratorData: any,
            origin: string,
            shop?: { code: string },
            onClose?: () => void,
        ): void => {
            const addToCartPromise = dispatch(
                addConfigurableProductToCart(
                    { ...(configuratorData || {}), shopId: shop?.code || null },
                    endPoint,
                ),
            );

            addToCartPromise
                .then((action: TApiAction) =>
                    sendSuccessMessage(
                        configuratorData,
                        iframeRef?.current?.contentWindow,
                        origin,
                        action,
                    ),
                )
                .catch(() =>
                    sendFailMessage(
                        configuratorData,
                        iframeRef?.current?.contentWindow,
                        origin,
                    ),
                )
                .finally(() => onClose && onClose());
        },
        [iframeRef, dispatch],
    );

    const handleOnStorePickerSubmit = useCallback(
        (store: StoreDetailsModel, onClose?: () => void) => {
            handleStoreSelect(cartData, cartMessage.origin, store, onClose);
        },
        [handleStoreSelect, cartData, cartMessage],
    );

    const handleOnStorePickerClose = useCallback(
        () => setStorePickerModalState(StorePickerModalStates.Closed),
        [setStorePickerModalState],
    );

    if (!iframeUrl || !endPoint) return <></>;

    return (
        <>
            <iframe
                className={"configurable-component__iframe"}
                ref={iframeRef}
                src={iframeUrl}
                width="100%"
                height="800"
                scrolling="no"
            ></iframe>
            <StorePickerModal
                titleTK="configurableProduct.storepicker.title"
                subTitleTK="configurableProduct.storepicker.subTitle"
                type={StorePickerType.SELECT}
                infoLink={null}
                submitButtonText={
                    storePickerModalState === StorePickerModalStates.AddToCart
                        ? t("general.storepicker.button.confirmStoreAndOrder")
                        : ""
                }
                isOpen={storePickerModalState !== StorePickerModalStates.Closed}
                storeCode={preferredStore?.code ?? null}
                fixedFeatures={FixedFeatures}
                displayStockLevel={false}
                onSubmit={handleOnStorePickerSubmit}
                onClose={handleOnStorePickerClose}
            />
        </>
    );
};

export { ConfigurableProductComponent };
